import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type Chart,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import {
  CHART_ALERT_COLOR,
  CHART_AXIS_TEXT_COLOR,
  CHART_GRID_COLOR,
  CHART_SERIES_COLOR,
} from "~/lib/chartPalette";
import { cn } from "~/lib/utils";
import { SearchChunkSizeBucket } from "~/services/search";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const CHART_HEIGHT_CLS = "h-[260px]";
const LABEL_FONT = "600 10px system-ui, -apple-system, sans-serif";

/** A bin's full range, for the tooltip. */
const bucketLabel = (bucket: SearchChunkSizeBucket): string =>
  bucket.upper == null ? `${bucket.lower}+` : `${bucket.lower}–${bucket.upper}`;

/** A bin's lower edge alone, since a dozen full ranges collide on the axis. */
const bucketTick = (bucket: SearchChunkSizeBucket): string =>
  bucket.upper == null ? `${bucket.lower}+` : String(bucket.lower);

/** The count above each bar, empty bins left unlabelled. */
const barCountPlugin = (color: string): Plugin<"bar"> => ({
  id: "searchChunkCounts",
  afterDatasetsDraw(chart: Chart<"bar">) {
    const { ctx } = chart;
    ctx.save();
    ctx.font = LABEL_FONT;
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    chart.getDatasetMeta(0).data.forEach((element, index) => {
      const value = chart.data.datasets[0].data[index];
      if (typeof value === "number" && value > 0) {
        ctx.fillText(value.toLocaleString(), element.x, element.y - 5);
      }
    });
    ctx.restore();
  },
});

/** A dashed rule at the chunker's max size, since colour alone does not say where it is. */
const maxSizePlugin = (buckets: SearchChunkSizeBucket[], color: string): Plugin<"bar"> => ({
  id: "searchChunkMaxSize",
  afterDatasetsDraw(chart: Chart<"bar">) {
    const index = buckets.findIndex((bucket) => bucket.over_max);
    if (index <= 0) return;
    const { ctx, chartArea, scales } = chart;
    const half = scales.x.width / buckets.length / 2;
    const x = scales.x.getPixelForValue(index) - half;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.font = LABEL_FONT;
    ctx.fillStyle = color;
    ctx.textAlign = "right";
    ctx.textBaseline = "top";
    ctx.fillText(`${buckets[index].lower} limit`, x - 4, chartArea.top);
    ctx.restore();
  },
});

const chartOptions = (
  buckets: SearchChunkSizeBucket[],
  total: number,
  axisTextColor: string,
  gridColor: string
): ChartOptions<"bar"> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  // Room for the count and max-size labels drawn above the plot.
  layout: { padding: { top: 20 } },
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: {
        title: (items) => `${bucketLabel(buckets[items[0].dataIndex])} characters`,
        label: (item) => {
          const chunks = item.parsed.y ?? 0;
          const share = Math.round((chunks / total) * 100);
          return `${chunks.toLocaleString()} ${chunks === 1 ? "chunk" : "chunks"} · ${share}%`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        color: axisTextColor,
        font: { size: 10 },
        // Edges read as a scale only when they stay horizontal and evenly cut.
        maxRotation: 0,
        autoSkipPadding: 12,
      },
      grid: { display: false },
      border: { display: false },
    },
    y: {
      min: 0,
      ticks: { display: false },
      // Gridlines without axis numbers, so similar bars stay comparable.
      grid: { color: gridColor, drawTicks: false },
      border: { display: false },
    },
  },
});

/**
 * How long the indexed chunks are, in characters.
 *
 * Bunched at the low end means the max size is irrelevant and retrieval is
 * matching titles; bunched at the max means sections are being cut.
 */
export const ChunkSizes = ({ buckets }: { buckets: SearchChunkSizeBucket[] }) => {
  const isDark = useIsDarkMode();
  const mode = isDark ? "dark" : "light";
  const axisTextColor = CHART_AXIS_TEXT_COLOR[mode];
  const total = buckets.reduce((sum, bucket) => sum + bucket.chunks, 0);

  if (total === 0) {
    return (
      <div
        className={cn(
          CHART_HEIGHT_CLS,
          "flex items-center justify-center text-sm text-muted-foreground"
        )}
      >
        Nothing indexed yet.
      </div>
    );
  }

  return (
    <div className={CHART_HEIGHT_CLS}>
      <Bar
        data={{
          labels: buckets.map(bucketTick),
          datasets: [
            {
              label: "Chunks",
              data: buckets.map((bucket) => bucket.chunks),
              backgroundColor: buckets.map((bucket) =>
                bucket.over_max ? CHART_ALERT_COLOR[mode] : CHART_SERIES_COLOR[mode]
              ),
              borderRadius: 2,
              borderSkipped: "start",
              // Near-touching: the bins are a continuous range.
              categoryPercentage: 1,
              barPercentage: 0.92,
            },
          ],
        }}
        options={chartOptions(buckets, total, axisTextColor, CHART_GRID_COLOR[mode])}
        plugins={[barCountPlugin(axisTextColor), maxSizePlugin(buckets, axisTextColor)]}
      />
    </div>
  );
};
